/**
 * Settings Scene
 * Game configuration and accessibility options
 * Bioluminescent microscopy aesthetic
 */

import Phaser from 'phaser';
import { getAccessibilityManager } from '@accessibility/AccessibilityManager';
import type { ColorBlindMode } from '@accessibility/ColorBlindFilters';
import type { FontSizeLevel } from '@accessibility/FontSizeManager';

const COLORS = {
  deepOcean: 0x040d1a,
  darkBlue: 0x0a1628,
  midBlue: 0x0f2847,
  accentCyan: 0x00d4ff,
  accentTeal: 0x00ffd4,
  accentGreen: 0x00ff88,
  accentPurple: 0xb366ff,
  accentGold: 0xffd700,
  accentRed: 0xff6b6b,
  textPrimary: 0xe8f4f8,
  textSecondary: 0x8ba4b4,
  textMuted: 0x4a6070,
};

const STORAGE_KEY = 'cellsandlife_settings';

interface GameSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  colorBlindMode: 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
  reduceMotion: boolean;
  adaptiveDifficulty: boolean;
  showHints: boolean;
}

const DEFAULT_SETTINGS: GameSettings = {
  masterVolume: 0.8,
  musicVolume: 0.7,
  sfxVolume: 0.8,
  colorBlindMode: 'none',
  fontSize: 'medium',
  reduceMotion: false,
  adaptiveDifficulty: true,
  showHints: true,
};

function loadSettings(): GameSettings {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.warn('Failed to load settings:', e);
  }
  return { ...DEFAULT_SETTINGS };
}

function saveSettings(settings: GameSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('Failed to save settings:', e);
  }
}

export class SettingsScene extends Phaser.Scene {
  private settings!: GameSettings;
  private scrollY = 0;
  private maxScroll = 0;
  private contentContainer!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'SettingsScene' });
  }

  create(): void {
    this.settings = loadSettings();
    this.scrollY = 0;

    this.createBackground();
    this.createHeader();
    this.createSettingsContent();
    this.createBackButton();
    this.setupScrolling();
  }

  private createBackground(): void {
    const { width, height } = this.cameras.main;
    const bg = this.add.graphics();

    // Deep ocean base
    bg.fillStyle(COLORS.deepOcean, 1);
    bg.fillRect(0, 0, width, height);

    // Vertical gradient
    for (let i = 0; i < 30; i++) {
      const ratio = i / 30;
      const y = ratio * height;
      const alpha = 0.02 + Math.sin(ratio * Math.PI) * 0.025;
      bg.fillStyle(COLORS.midBlue, alpha);
      bg.fillRect(0, y, width, height / 30 + 1);
    }

    // Radial glow
    for (let i = 20; i >= 0; i--) {
      const radius = (width * 0.4) * (i / 20);
      const alpha = 0.01 * (1 - i / 20);
      bg.fillStyle(COLORS.accentCyan, alpha);
      bg.fillCircle(width / 2, height * 0.3, radius);
    }

    // Corner glows
    const glowSpots = [
      { x: 0, y: height, color: COLORS.accentPurple, size: 250 },
      { x: width, y: 0, color: COLORS.accentTeal, size: 200 },
    ];

    glowSpots.forEach((glow) => {
      for (let i = 15; i >= 0; i--) {
        const radius = (i / 15) * glow.size;
        const alpha = 0.006 * (1 - i / 15);
        bg.fillStyle(glow.color, alpha);
        bg.fillCircle(glow.x, glow.y, radius);
      }
    });

    // Floating particles
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = 1 + Math.random() * 2;
      const color = [COLORS.accentCyan, COLORS.accentTeal, COLORS.accentPurple][Math.floor(Math.random() * 3)];

      bg.fillStyle(color, 0.15 + Math.random() * 0.2);
      bg.fillCircle(x, y, size);
    }
  }

  private createHeader(): void {
    const { width } = this.cameras.main;

    // Title
    const title = this.add.text(width / 2, 50, 'SETTINGS', {
      fontFamily: '"Courier New", monospace',
      fontSize: '42px',
      fontStyle: 'bold',
      color: '#00d4ff',
    }).setOrigin(0.5);
    title.setStroke('#001a2e', 4);
    title.setShadow(0, 0, '#00d4ff', 15, true, true);

    // Decorative line
    const line = this.add.graphics();
    line.lineStyle(2, COLORS.accentCyan, 0.4);
    line.lineBetween(width / 2 - 120, 85, width / 2 + 120, 85);

    // Diamond accents
    line.fillStyle(COLORS.accentCyan, 0.6);
    line.fillRect(width / 2 - 4, 81, 8, 8);
  }

  private createSettingsContent(): void {
    const { height } = this.cameras.main;
    this.contentContainer = this.add.container(0, 120);

    let yOffset = 0;

    // Audio Section
    yOffset = this.createSectionHeader('AUDIO', yOffset);
    yOffset = this.createSlider('Master Volume', this.settings.masterVolume, yOffset, (val) => {
      this.settings.masterVolume = val;
      this.saveAndApply();
    });
    yOffset = this.createSlider('Music Volume', this.settings.musicVolume, yOffset, (val) => {
      this.settings.musicVolume = val;
      this.saveAndApply();
    });
    yOffset = this.createSlider('Sound Effects', this.settings.sfxVolume, yOffset, (val) => {
      this.settings.sfxVolume = val;
      this.saveAndApply();
    });

    yOffset += 20;

    // Accessibility Section
    yOffset = this.createSectionHeader('ACCESSIBILITY', yOffset);
    yOffset = this.createDropdown('Color Blind Mode', [
      { value: 'none', label: 'None' },
      { value: 'protanopia', label: 'Protanopia (Red)' },
      { value: 'deuteranopia', label: 'Deuteranopia (Green)' },
      { value: 'tritanopia', label: 'Tritanopia (Blue)' },
    ], this.settings.colorBlindMode, yOffset, (val) => {
      this.settings.colorBlindMode = val as GameSettings['colorBlindMode'];
      this.saveAndApply();
    });

    yOffset = this.createDropdown('Font Size', [
      { value: 'small', label: 'Small' },
      { value: 'medium', label: 'Medium' },
      { value: 'large', label: 'Large' },
      { value: 'xlarge', label: 'Extra Large' },
    ], this.settings.fontSize, yOffset, (val) => {
      this.settings.fontSize = val as GameSettings['fontSize'];
      this.saveAndApply();
    });

    yOffset = this.createToggle('Reduce Motion', this.settings.reduceMotion, yOffset, (val) => {
      this.settings.reduceMotion = val;
      this.saveAndApply();
    });

    yOffset += 20;

    // Gameplay Section
    yOffset = this.createSectionHeader('GAMEPLAY', yOffset);
    yOffset = this.createToggle('Adaptive Difficulty', this.settings.adaptiveDifficulty, yOffset, (val) => {
      this.settings.adaptiveDifficulty = val;
      this.saveAndApply();
    });
    yOffset = this.createToggle('Show Hints', this.settings.showHints, yOffset, (val) => {
      this.settings.showHints = val;
      this.saveAndApply();
    });

    yOffset += 30;

    // Danger Zone
    yOffset = this.createSectionHeader('DATA', yOffset, COLORS.accentRed);
    yOffset = this.createDangerButton('Reset All Progress', yOffset, () => this.confirmReset());

    // Calculate max scroll
    this.maxScroll = Math.max(0, yOffset - (height - 180));
  }

  private createSectionHeader(text: string, yOffset: number, color: number = COLORS.accentCyan): number {
    const { width } = this.cameras.main;

    const header = this.add.text(width / 2, yOffset, text, {
      fontFamily: '"Courier New", monospace',
      fontSize: '16px',
      fontStyle: 'bold',
      color: Phaser.Display.Color.IntegerToColor(color).rgba,
      letterSpacing: 3,
    }).setOrigin(0.5);

    // Underline
    const line = this.add.graphics();
    line.lineStyle(1, color, 0.3);
    line.lineBetween(width / 2 - 100, yOffset + 15, width / 2 + 100, yOffset + 15);

    this.contentContainer.add([header, line]);

    return yOffset + 45;
  }

  private createSlider(
    label: string,
    value: number,
    yOffset: number,
    onChange: (value: number) => void
  ): number {
    const { width } = this.cameras.main;
    const sliderWidth = 200;
    const sliderX = width / 2 + 40;

    // Label
    const labelText = this.add.text(width / 2 - 180, yOffset, label, {
      fontFamily: 'Georgia, serif',
      fontSize: '15px',
      color: '#e8f4f8',
    });

    // Slider track
    const track = this.add.graphics();
    track.fillStyle(COLORS.darkBlue, 1);
    track.fillRoundedRect(sliderX, yOffset + 3, sliderWidth, 10, 5);
    track.lineStyle(1, COLORS.accentCyan, 0.3);
    track.strokeRoundedRect(sliderX, yOffset + 3, sliderWidth, 10, 5);

    // Slider fill
    const fill = this.add.graphics();
    this.drawSliderFill(fill, sliderX, yOffset + 3, sliderWidth, value);

    // Slider handle
    const handleX = sliderX + value * sliderWidth;
    const handle = this.add.graphics();
    this.drawSliderHandle(handle, handleX, yOffset + 8, false);

    // Value text
    const valueText = this.add.text(sliderX + sliderWidth + 15, yOffset, `${Math.round(value * 100)}%`, {
      fontFamily: '"Courier New", monospace',
      fontSize: '13px',
      color: '#00d4ff',
    });

    // Interactive zone
    const hitArea = this.add.rectangle(sliderX + sliderWidth / 2, yOffset + 8, sliderWidth + 20, 24, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.updateSlider(pointer, sliderX, sliderWidth, fill, handle, valueText, yOffset, onChange);
    });

    hitArea.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) {
        this.updateSlider(pointer, sliderX, sliderWidth, fill, handle, valueText, yOffset, onChange);
      }
    });

    hitArea.on('pointerover', () => this.drawSliderHandle(handle, handle.x, yOffset + 8, true));
    hitArea.on('pointerout', () => this.drawSliderHandle(handle, handle.x, yOffset + 8, false));

    this.contentContainer.add([labelText, track, fill, handle, valueText, hitArea]);

    return yOffset + 45;
  }

  private updateSlider(
    pointer: Phaser.Input.Pointer,
    sliderX: number,
    sliderWidth: number,
    fill: Phaser.GameObjects.Graphics,
    handle: Phaser.GameObjects.Graphics,
    valueText: Phaser.GameObjects.Text,
    yOffset: number,
    onChange: (value: number) => void
  ): void {
    const localX = pointer.x - sliderX;
    const newValue = Phaser.Math.Clamp(localX / sliderWidth, 0, 1);

    this.drawSliderFill(fill, sliderX, yOffset + 3, sliderWidth, newValue);
    handle.x = sliderX + newValue * sliderWidth;
    this.drawSliderHandle(handle, 0, 0, true);
    valueText.setText(`${Math.round(newValue * 100)}%`);

    onChange(newValue);
  }

  private drawSliderFill(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    width: number,
    value: number
  ): void {
    graphics.clear();
    if (value > 0.02) {
      graphics.fillStyle(COLORS.accentCyan, 0.6);
      graphics.fillRoundedRect(x, y, width * value, 10, 5);
    }
  }

  private drawSliderHandle(graphics: Phaser.GameObjects.Graphics, x: number, y: number, hover: boolean): void {
    graphics.clear();
    graphics.x = x;
    graphics.y = y;

    if (hover) {
      graphics.fillStyle(COLORS.accentCyan, 0.3);
      graphics.fillCircle(0, 0, 12);
    }
    graphics.fillStyle(COLORS.accentCyan, 1);
    graphics.fillCircle(0, 0, 7);
    graphics.fillStyle(0xffffff, 0.3);
    graphics.fillCircle(-2, -2, 3);
  }

  private createToggle(
    label: string,
    value: boolean,
    yOffset: number,
    onChange: (value: boolean) => void
  ): number {
    const { width } = this.cameras.main;

    // Label
    const labelText = this.add.text(width / 2 - 180, yOffset, label, {
      fontFamily: 'Georgia, serif',
      fontSize: '15px',
      color: '#e8f4f8',
    });

    // Toggle background
    const toggleX = width / 2 + 140;
    const toggle = this.add.graphics();
    this.drawToggle(toggle, toggleX, yOffset + 8, value);

    // Interactive
    const hitArea = this.add.rectangle(toggleX + 22, yOffset + 8, 54, 28, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });

    let currentValue = value;

    hitArea.on('pointerdown', () => {
      currentValue = !currentValue;
      this.drawToggle(toggle, toggleX, yOffset + 8, currentValue);
      onChange(currentValue);
    });

    this.contentContainer.add([labelText, toggle, hitArea]);

    return yOffset + 45;
  }

  private drawToggle(graphics: Phaser.GameObjects.Graphics, x: number, y: number, on: boolean): void {
    graphics.clear();

    // Track
    graphics.fillStyle(on ? COLORS.accentCyan : COLORS.darkBlue, on ? 0.4 : 1);
    graphics.fillRoundedRect(x, y - 10, 44, 20, 10);
    graphics.lineStyle(1, on ? COLORS.accentCyan : COLORS.textMuted, 0.5);
    graphics.strokeRoundedRect(x, y - 10, 44, 20, 10);

    // Handle
    const handleX = on ? x + 28 : x + 16;
    graphics.fillStyle(on ? COLORS.accentCyan : COLORS.textSecondary, 1);
    graphics.fillCircle(handleX, y, 8);

    if (on) {
      graphics.fillStyle(0xffffff, 0.3);
      graphics.fillCircle(handleX - 2, y - 2, 3);
    }
  }

  private createDropdown(
    label: string,
    options: { value: string; label: string }[],
    currentValue: string,
    yOffset: number,
    onChange: (value: string) => void
  ): number {
    const { width } = this.cameras.main;
    const dropdownWidth = 180;
    const dropdownX = width / 2 + 60;

    // Label
    const labelText = this.add.text(width / 2 - 180, yOffset, label, {
      fontFamily: 'Georgia, serif',
      fontSize: '15px',
      color: '#e8f4f8',
    });

    // Current selection display
    const currentOption = options.find((o) => o.value === currentValue) || options[0];
    const bg = this.add.graphics();
    this.drawDropdownBg(bg, dropdownX, yOffset - 5, dropdownWidth, false);

    const selectedText = this.add.text(dropdownX + 12, yOffset, currentOption.label, {
      fontFamily: '"Courier New", monospace',
      fontSize: '13px',
      color: '#e8f4f8',
    });

    // Arrow
    const arrow = this.add.text(dropdownX + dropdownWidth - 20, yOffset, '▼', {
      fontFamily: 'sans-serif',
      fontSize: '10px',
      color: '#00d4ff',
    });

    // Hit area
    const hitArea = this.add.rectangle(dropdownX + dropdownWidth / 2, yOffset + 8, dropdownWidth, 28, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });

    let isOpen = false;
    let optionElements: Phaser.GameObjects.GameObject[] = [];

    const closeDropdown = () => {
      if (isOpen) {
        optionElements.forEach((el) => el.destroy());
        optionElements = [];
        isOpen = false;
        arrow.setText('▼');
      }
    };

    hitArea.on('pointerdown', () => {
      if (isOpen) {
        closeDropdown();
        return;
      }

      isOpen = true;
      arrow.setText('▲');

      options.forEach((option, i) => {
        const optY = yOffset + 30 + i * 28;

        const optBg = this.add.graphics();
        optBg.fillStyle(COLORS.darkBlue, 0.95);
        optBg.fillRect(dropdownX, optY - 5, dropdownWidth, 28);
        optBg.lineStyle(1, COLORS.accentCyan, 0.2);
        optBg.strokeRect(dropdownX, optY - 5, dropdownWidth, 28);

        const optText = this.add.text(dropdownX + 12, optY, option.label, {
          fontFamily: '"Courier New", monospace',
          fontSize: '13px',
          color: option.value === currentValue ? '#00d4ff' : '#e8f4f8',
        });

        const optHit = this.add.rectangle(dropdownX + dropdownWidth / 2, optY + 8, dropdownWidth, 28, 0x000000, 0);
        optHit.setInteractive({ useHandCursor: true });

        optHit.on('pointerover', () => {
          optBg.clear();
          optBg.fillStyle(COLORS.midBlue, 1);
          optBg.fillRect(dropdownX, optY - 5, dropdownWidth, 28);
          optText.setColor('#00ffd4');
        });

        optHit.on('pointerout', () => {
          optBg.clear();
          optBg.fillStyle(COLORS.darkBlue, 0.95);
          optBg.fillRect(dropdownX, optY - 5, dropdownWidth, 28);
          optText.setColor(option.value === currentValue ? '#00d4ff' : '#e8f4f8');
        });

        optHit.on('pointerdown', () => {
          selectedText.setText(option.label);
          onChange(option.value);
          closeDropdown();
        });

        optionElements.push(optBg, optText, optHit);
        this.contentContainer.add([optBg, optText, optHit]);
      });
    });

    hitArea.on('pointerover', () => this.drawDropdownBg(bg, dropdownX, yOffset - 5, dropdownWidth, true));
    hitArea.on('pointerout', () => this.drawDropdownBg(bg, dropdownX, yOffset - 5, dropdownWidth, false));

    this.contentContainer.add([labelText, bg, selectedText, arrow, hitArea]);

    return yOffset + 45;
  }

  private drawDropdownBg(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    width: number,
    hover: boolean
  ): void {
    graphics.clear();
    graphics.fillStyle(hover ? COLORS.midBlue : COLORS.darkBlue, 0.95);
    graphics.fillRoundedRect(x, y, width, 28, 6);
    graphics.lineStyle(1, COLORS.accentCyan, hover ? 0.6 : 0.3);
    graphics.strokeRoundedRect(x, y, width, 28, 6);
  }

  private createDangerButton(label: string, yOffset: number, onClick: () => void): number {
    const { width } = this.cameras.main;
    const btnWidth = 200;
    const btnX = width / 2 - btnWidth / 2;

    const container = this.add.container(btnX, yOffset);

    const bg = this.add.graphics();
    this.drawDangerButton(bg, btnWidth, false);

    const text = this.add.text(btnWidth / 2, 18, label, {
      fontFamily: '"Courier New", monospace',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ff6b6b',
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(btnWidth, 36);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      this.drawDangerButton(bg, btnWidth, true);
      text.setColor('#ffffff');
    });

    container.on('pointerout', () => {
      this.drawDangerButton(bg, btnWidth, false);
      text.setColor('#ff6b6b');
    });

    container.on('pointerdown', onClick);

    this.contentContainer.add(container);

    return yOffset + 50;
  }

  private drawDangerButton(graphics: Phaser.GameObjects.Graphics, width: number, hover: boolean): void {
    graphics.clear();
    graphics.fillStyle(hover ? 0x8b0000 : COLORS.darkBlue, 0.95);
    graphics.fillRoundedRect(0, 0, width, 36, 8);
    graphics.lineStyle(1, COLORS.accentRed, hover ? 0.8 : 0.4);
    graphics.strokeRoundedRect(0, 0, width, 36, 8);
  }

  private confirmReset(): void {
    const { width, height } = this.cameras.main;

    // Overlay
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.7);
    overlay.fillRect(0, 0, width, height);
    overlay.setDepth(100);

    // Dialog
    const dialog = this.add.container(width / 2, height / 2);
    dialog.setDepth(101);

    const dialogBg = this.add.graphics();
    dialogBg.fillStyle(COLORS.darkBlue, 0.98);
    dialogBg.fillRoundedRect(-180, -100, 360, 200, 12);
    dialogBg.lineStyle(2, COLORS.accentRed, 0.6);
    dialogBg.strokeRoundedRect(-180, -100, 360, 200, 12);

    const title = this.add.text(0, -70, 'RESET PROGRESS?', {
      fontFamily: '"Courier New", monospace',
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#ff6b6b',
    }).setOrigin(0.5);

    const message = this.add.text(0, -20, 'This will delete all your learning\nprogress and cannot be undone.', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#e8f4f8',
      align: 'center',
    }).setOrigin(0.5);

    // Cancel button
    const cancelBtn = this.createDialogButton(-70, 50, 'CANCEL', COLORS.accentCyan, () => {
      overlay.destroy();
      dialog.destroy();
    });

    // Confirm button
    const confirmBtn = this.createDialogButton(70, 50, 'RESET', COLORS.accentRed, () => {
      this.resetAllProgress();
      overlay.destroy();
      dialog.destroy();
    });

    dialog.add([dialogBg, title, message, cancelBtn, confirmBtn]);
  }

  private createDialogButton(
    x: number,
    y: number,
    label: string,
    color: number,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.midBlue, 1);
    bg.fillRoundedRect(-55, -18, 110, 36, 6);
    bg.lineStyle(1, color, 0.5);
    bg.strokeRoundedRect(-55, -18, 110, 36, 6);

    const text = this.add.text(0, 0, label, {
      fontFamily: '"Courier New", monospace',
      fontSize: '13px',
      fontStyle: 'bold',
      color: Phaser.Display.Color.IntegerToColor(color).rgba,
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(110, 36);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color, 0.3);
      bg.fillRoundedRect(-55, -18, 110, 36, 6);
      bg.lineStyle(1, color, 0.8);
      bg.strokeRoundedRect(-55, -18, 110, 36, 6);
    });

    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(COLORS.midBlue, 1);
      bg.fillRoundedRect(-55, -18, 110, 36, 6);
      bg.lineStyle(1, color, 0.5);
      bg.strokeRoundedRect(-55, -18, 110, 36, 6);
    });

    container.on('pointerdown', onClick);

    return container;
  }

  private resetAllProgress(): void {
    // Clear all localStorage keys for the game
    const keysToRemove = [
      'cells_and_life_progress',
      'cellsandlife_assessment',
      'cellsandlife_difficulty',
      'cellsandlife_analytics',
    ];

    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Show confirmation
    this.showNotification('Progress has been reset');
  }

  private showNotification(message: string): void {
    const { width, height } = this.cameras.main;

    const notification = this.add.container(width / 2, height - 40);
    notification.setDepth(200);

    const bg = this.add.graphics();
    bg.fillStyle(0x1a4030, 0.95);
    bg.fillRoundedRect(-120, -20, 240, 40, 8);
    bg.lineStyle(1, COLORS.accentGreen, 0.6);
    bg.strokeRoundedRect(-120, -20, 240, 40, 8);

    const text = this.add.text(0, 0, message, {
      fontFamily: '"Courier New", monospace',
      fontSize: '14px',
      color: '#00ff88',
    }).setOrigin(0.5);

    notification.add([bg, text]);
    notification.setAlpha(0);

    this.tweens.add({
      targets: notification,
      alpha: 1,
      y: height - 60,
      duration: 300,
      ease: 'Back.out',
      onComplete: () => {
        this.time.delayedCall(2000, () => {
          this.tweens.add({
            targets: notification,
            alpha: 0,
            duration: 300,
            onComplete: () => notification.destroy(),
          });
        });
      },
    });
  }

  private setupScrolling(): void {
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: unknown, _deltaX: number, deltaY: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + deltaY * 0.5, 0, this.maxScroll);
      this.contentContainer.setY(120 - this.scrollY);
    });
  }

  private createBackButton(): void {
    const container = this.add.container(30, 30);

    const glow = this.add.graphics();
    glow.fillStyle(COLORS.accentCyan, 0.08);
    glow.fillRoundedRect(-3, -3, 96, 42, 10);

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.darkBlue, 0.95);
    bg.fillRoundedRect(0, 0, 90, 36, 8);
    bg.lineStyle(1, COLORS.accentCyan, 0.5);
    bg.strokeRoundedRect(0, 0, 90, 36, 8);

    const text = this.add.text(45, 18, '← BACK', {
      fontFamily: '"Courier New", monospace',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#00d4ff',
    }).setOrigin(0.5);

    container.add([glow, bg, text]);
    container.setSize(90, 36);
    container.setInteractive({ useHandCursor: true });
    container.setDepth(1000);

    container.on('pointerover', () => {
      glow.clear();
      glow.fillStyle(COLORS.accentCyan, 0.15);
      glow.fillRoundedRect(-3, -3, 96, 42, 10);
      bg.clear();
      bg.fillStyle(COLORS.darkBlue, 1);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(2, COLORS.accentCyan, 0.8);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00ffd4');
    });

    container.on('pointerout', () => {
      glow.clear();
      glow.fillStyle(COLORS.accentCyan, 0.08);
      glow.fillRoundedRect(-3, -3, 96, 42, 10);
      bg.clear();
      bg.fillStyle(COLORS.darkBlue, 0.95);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(1, COLORS.accentCyan, 0.5);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00d4ff');
    });

    container.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
  }

  private saveAndApply(): void {
    saveSettings(this.settings);

    // Apply accessibility settings to the AccessibilityManager
    const a11y = getAccessibilityManager();
    a11y.setColorBlindMode(this.settings.colorBlindMode as ColorBlindMode);
    a11y.setFontSize(this.settings.fontSize as FontSizeLevel);
    a11y.setReduceMotion(this.settings.reduceMotion);

    // Apply settings could trigger events for other systems
    this.events.emit('settings:changed', this.settings);
  }
}
